// RSVP deadline predicate shared by the public invitation artwork
// (`InvitationArtwork`) and the RSVP page (`Confirmacion`).
//
// Mirrors the backend lock semantics in
// `backend/src/utils/date.js::isRsvpLocked`: the deadline day itself
// stays open until 23:59:59 Ecuador time (America/Guayaquil, UTC-5,
// no DST). The API answers POST /api/confirmacion/:token with a 423
// once that instant is gone, so the UI must stop asking guests to
// confirm at the same moment.
//
// FAILS OPEN: a missing or invalid deadline returns false — we never
// hide copy we cannot prove expired.
import { EVENT_DATA } from './event.js';

export function isRsvpDeadlinePassed(deadlineIso, now = new Date()) {
  if (!deadlineIso) return false;
  const lockAt = new Date(`${deadlineIso}T23:59:59-05:00`);
  // Unparseable deadline → fail open (never hide on a guess).
  if (Number.isNaN(lockAt.getTime())) return false;
  return now > lockAt;
}

// The effective deadline: the admin-editable DB value when it is
// available, the seeded value otherwise. The seed mirrors the DB row
// byte-for-byte, so the first synchronous render already agrees with
// what the async fetch resolves to (zero-CLS contract — see
// `frontend/src/lib/event.js`).
export function resolveRsvpDeadlineIso(eventSettings) {
  return eventSettings?.rsvp_deadline ?? EVENT_DATA.rsvp.deadline.iso;
}

// The authoritative "is the guest list closed?" gate. It closes the page
// ONLY on a deadline the server actually answered: the static seed is a
// text fallback, never a visibility source of truth, so an admin deadline
// change never needs a seed re-sync to take effect, and a failed or
// in-flight settings request keeps the invitation visible (fail open).
export function isRsvpClosed(eventSettings) {
  if (!eventSettings?.rsvp_deadline) return false;
  return isRsvpDeadlinePassed(resolveRsvpDeadlineIso(eventSettings));
}

// The confirmed-invitation window includes the wedding date and the next
// fourteen calendar days. Ecuador stays at UTC-5, so the cutoff is midnight
// at the start of day fifteen in that timezone.
export function getConfirmedInvitationCutoff(eventDateIso) {
  if (typeof eventDateIso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(eventDateIso)) {
    return null;
  }

  const [year, month, day] = eventDateIso.split('-').map(Number);
  const calendarDate = new Date(0);
  calendarDate.setUTCHours(0, 0, 0, 0);
  calendarDate.setUTCFullYear(year, month - 1, day);
  if (
    calendarDate.getUTCFullYear() !== year ||
    calendarDate.getUTCMonth() !== month - 1 ||
    calendarDate.getUTCDate() !== day
  ) {
    return null;
  }

  calendarDate.setUTCDate(calendarDate.getUTCDate() + 15);
  return new Date(calendarDate.getTime() + 5 * 60 * 60 * 1000);
}

export function isConfirmedInvitationVisible(eventDateIso, now = new Date()) {
  const cutoff = getConfirmedInvitationCutoff(eventDateIso);
  return cutoff === null || now < cutoff;
}
